import { useRolodexEntries } from 'features/rolodex'
import React from 'react'
import {
  DropdownOption,
  GeckoKestrelModel,
  GeckoKestrelModelUnit,
  GeckoUnit,
  KestrelInspectionType,
  RolodexEntryType,
} from 'types'

/**
 * Grabs shared unit/link data for Kestrel menus and re-triggers on model updates.
 * Returns site/org tags, site units, unit dropdown options, and kestrel-model-unit links.
 */
export const useKestrelMenuUnitData = (
  kestrelModel: GeckoKestrelModel | undefined
): {
  siteId: string | undefined
  orgId: string | undefined
  siteUnits: ReadonlyArray<GeckoUnit>
  unitOptions: DropdownOption[]
  kestrelModelUnits: ReadonlyArray<GeckoKestrelModelUnit>
} => {
  const isSiteview =
    kestrelModel?.data.inspection_type === KestrelInspectionType.SITEVIEW
  const siteId = kestrelModel?.tags.site as string | undefined
  const orgId = kestrelModel?.tags.organization as string | undefined

  // Site Units
  const { units: siteUnits } = useRolodexEntries(
    isSiteview && siteId
      ? {
          type: RolodexEntryType.UNIT,
          tags: { site: siteId },
          limit: 10000,
        }
      : undefined,
    { allowCache: true }
  )

  // Unit dropdown options
  const unitOptions: DropdownOption[] = React.useMemo(
    () =>
      siteUnits
        .map((u) => ({ value: u.id, label: u.data.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [siteUnits]
  )

  // Links between Kestrel model and units
  const { kestrelModelUnits } = useRolodexEntries(
    isSiteview && kestrelModel
      ? {
          type: RolodexEntryType.KESTREL_MODEL_UNIT,
          tags: {
            [RolodexEntryType.KESTREL_MODEL]: kestrelModel.id,
            ...(siteId ? { site: siteId } : {}),
          },
          limit: 10000,
        }
      : undefined,
    { allowCache: true }
  )

  return {
    siteId,
    orgId,
    siteUnits,
    unitOptions,
    kestrelModelUnits,
  }
}
